import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import icons from "./IconLoader";

function IntroHelpOverlay({ onClose }) {
    const steps = [
        {
            target: 'המקומיות',
            message: 'כאן תוכלו לבחור את האזורים שתרצו להכיר לעומק. ניתן לבחור מקומיות רלוונטיות מתוך רשימה, על פי תצוגת מפה או על פי מקומיות קרובות למיקומך',
        },
        {
            target: 'מה תוכלו למצוא בכל מקומית?',
            isCustom: true,
        },
        {
            target: 'רשימות',
            message:
                'המקומית מאפשרת לכם לשמור כל דבר שמעניין אתכם ברשימות מועדפים ובכך לשמור את החוויות המקומיות שלכם וגם לשתף אותן עם חברים.ות!',
        },
        {
            target: '+',
            message: 'רישום עסק, מפגש או פוסט במקומיות הרלוונטיות לכם.',
        },
        {
            target: 'המידע שלי',
            message: 'העסקים | מפגשים | פוסטים | קבוצות שיצרתם במקום מרוכז אחד.',
        },
        {
            target: '🎯 לסיכום',
            message: 'כעת אתם מוכנים לצלול למקומית, להרגיש מקומיים, לגלות אזורים, קבוצות, מפגשים ועסקים — ולשתף את מה שמרגש אתכם עם מי שתרצו 🧡',
        }
    ];

    const [stepIndex, setStepIndex] = useState(-1); // -1 for intro
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const sessionSeen = sessionStorage.getItem('introSeenSession');
        const cookieSeen = document.cookie.includes('introSeen=true');

        if (cookieSeen) return;

        if (!sessionSeen) {
            sessionStorage.setItem('introSeenSession', 'true');
            setVisible(true);
        }
    }, []);

    if (!visible) return null;

    const handleClose = () => {
        if (dontShowAgain) {
            document.cookie = 'introSeen=true; path=/; max-age=31536000';
        }
        setVisible(false);
        onClose();
    };

    const handleNext = () => {
        if (stepIndex < steps.length - 1) {
            setStepIndex(stepIndex + 1);
        } else {
            handleClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-white text-black rounded-2xl p-6 max-w-md w-[90%] shadow-xl relative border border-warning-500"
            >
                {/*<p className="text-2xl font-bold text-warning-600">{steps[stepIndex].target}</p>*/}

                {/* Step list (only after intro) */}
                {stepIndex >= 0 && (
                    <div className="flex justify-start items-start mb-4">
                        <div className="relative w-full bg-warning-50 rounded-xl border border-warning-100 flex flex-col items-start p-4">
                            {steps.map((s, i) => (
                                <div
                                    key={i}
                                    className={`w-full mb-2 p-2 rounded-lg text-right text-sm font-bold shadow-sm ${i === stepIndex
                                        ? 'bg-info-100 text-black'
                                        : 'bg-white border border-warning-100 text-ink'
                                        }`}
                                >
                                    {s.target}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mb-4 text-center">
                    {stepIndex === -1 ? (
                        <>
                            <p className="text-2xl font-bold text-warning-600">ברוכים הבאים למקומית</p>
                            <p className="mt-3 text-base whitespace-pre-line leading-relaxed text-ink-strong">
                                באמצעות המקומית תוכלו להכיר יותר טוב את הקהילות שאתם חיים או מבקרים בהן, החל מקבוצות הוואטסאפ המקומיות ועד לעסקים ומפגשים.
                                <br />
                                <br />
                                <strong>להרגיש מקומי, בכל מקום</strong>
                                <br />
                                <br />
                                אתן לכם כעת הסבר קצרצר על מה תוכלו למצוא במקומית
                            </p>
                        </>
                    ) : (
                        <>
                            {steps[stepIndex].isCustom ? (
                                <div className="space-y-4 mt-4 text-right">
                                    <div className="flex items-center gap-3">
                                        <img src={icons.businessButton} alt="עסקים" className="w-12 h-12" />
                                        <span className="text-sm text-ink-strong font-semibold">עסקים – העסקים המקומיים הפועלים באזור</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <img src={icons.eventButton} alt="מפגשים" className="w-12 h-12" />
                                        <span className="text-sm text-ink-strong font-semibold">מפגשים – המפגשים שמתקיימים באזור</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <img src={icons.groups} alt="קבוצות" className="w-12 h-12" />
                                        <span className="text-sm text-ink-strong font-semibold">קבוצות – קבוצות הוואטסאפ, טלגרם וכו' שפועלות באזור</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <img src={icons.communityPostButton} alt="פוסטים" className="w-12 h-12" />
                                        <span className="text-sm text-ink-strong font-semibold">פוסטים – שיתופים קהילתיים</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <img src={icons.updateGroups} alt="ערוצי עדכון" className="w-12 h-12" />
                                        <span className="text-sm text-ink-strong font-semibold">ערוצי וואטסאפ וטלגרם עבור העדכונים החשובים מעסקים ומפגשים בכל אזור. כך תמיד תוכלו להשאר מעודכנים בכל תחום שמעניין אתכם</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {steps[stepIndex].target === 'המקומיות' ? (
                                        <div className="space-y-4 text-right">
                                            <p className="text-base whitespace-pre-line leading-relaxed text-ink-strong">
                                                {steps[stepIndex].message}
                                            </p>
                                            <div className="grid grid-cols-3 gap-3 mt-2">
                                                <div className="flex flex-col items-center">
                                                    <img src={zoneList} alt="בחירה מרשימה" className="w-32 h-32 rounded-lg border" />
                                                    <span className="text-xs mt-1">מתוך רשימה</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <img src={zoneNearby} alt="אזורים קרובים" className="w-26 h-32 rounded-lg border" />
                                                    <span className="text-xs mt-1">באזור שלך</span>
                                                </div>
                                                <div className="flex flex-col items-center">
                                                    <img src={zoneMap} alt="תצוגת מפה" className="w-26 h-32 rounded-lg border" />
                                                    <span className="text-xs mt-1">על גבי מפה</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : steps[stepIndex].target === 'רשימות' ? (
                                        <div className="space-y-4 text-right">
                                            <p className="text-base whitespace-pre-line leading-relaxed text-ink-strong">
                                                {steps[stepIndex].message}
                                            </p>
                                            <div className="flex justify-center mt-2">
                                                <img src={listsIntro} alt="רשימות מועדפים" className="w-32 h-38 rounded-lg border" />
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="mt-3 text-base whitespace-pre-line leading-relaxed text-ink-strong">
                                            {steps[stepIndex].message}
                                        </p>
                                    )}
                                </>
                            )}                        </>
                    )}
                </div>



                <div className="flex justify-between items-center">
                    <label className="flex items-center gap-2 text-xs text-ink">
                        <input
                            type="checkbox"
                            checked={dontShowAgain}
                            onChange={(e) => setDontShowAgain(e.target.checked)}
                        />
                        אל תראה את ההסבר שוב
                    </label>
                    <div className="flex gap-2">
                        <button
                            className="text-sm font-bold text-danger-600 hover:underline"
                            onClick={handleClose}
                        >
                            סגור
                        </button>
                        <button
                            className="text-sm font-bold bg-warning-500 text-white px-3 py-1 rounded hover:bg-warning-600"
                            onClick={handleNext}
                        >
                            {stepIndex === steps.length - 1 ? 'סיום' : 'הבא'}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

export default IntroHelpOverlay;
